import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import { FaCar, FaEdit, FaUserEdit, FaUserPlus } from 'react-icons/fa';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from 'antd';

import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';

import {
  Customer,
  CustomerReduced,
  GenderType,
  PersonType,
} from '@/core/domain/people/Customers/Customers.types';

import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementStatusNumber,
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';

import { CustomerVehicleReduced } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';

import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import CustomerSimplifiedRegisterModal from '@/components/Customers/CustomerSimplifiedRegisterModal';
import WorkOrderFormSummary from './WorkOrderFormSummary';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import CustomersSelectField from '@/components/_common/CustomersSelectField';
import VehicleSelectField from '@/components/_common/VehicleSelectField';
import InsurerInfiniteSelectField from '@/components/_common/InsurerInfiniteSelectField';
import Authorities from '@/types/Authority';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import CustomerDefaulterAlert from './CustomerDefaulterAlert';
import { useWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import WorkOrdersListModal from '@/components/_common/WorkOrdersListModal';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import './style.less';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import {
  usePortoPoliciesList,
  usePortoPolicieVoucherList,
} from '@/core/domain/porto/policies/porto-policies.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { isAppPortocar } from '@/index';
import WorkOrderComplementTypeSelect from '@/components/_common/WorkOrderComplementTypeSelect';
import {
  PortoPolicy,
  PortoPolicyVoucher,
} from '@/core/domain/porto/policies/porto-policies.types';
import EstimatedLeaveDateField from './EstimatedLeaveDateField';
import PortoPolicyCard from '@/components/_common/PortoPolicyCard';
import PortoVoucherList from './PortoVoucherList';
import PortoCPFCustomer from './PortoCPFCustomer';
import validateCpf from '@/utils/functions/validateCpf';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import StatusTag from '@/components/_common/StatusTag';

const { Title } = Typography;

export interface WorkOrderFormData {
  companyId: number;
  estimatorId: number;
  workOrderType: WorkOrderComplementType;
  customerType: CustomerType;
  insurerId?: number;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  entryDate?: Moment;
  estimatedLeaveDate?: Moment;
  customerId: number;
  vehicleId?: number;
  licensePlate?: string;
  prisma?: string;
  currentKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  policy?: {
    companyCode?: string;
    policyNumber?: string;
    customer?: {
      birthDate?: string;
      documentNumber?: string;
      contacts?: { email: string; phone: string };
      name?: string;
    };
    vehicle?: {
      chassi?: string;
      model?: string;
      brand?: string;
      yearFabrication?: number;
      yearModel?: number;
    };
    benefits: PortoPolicy['benefits'];
  };
  lastOilChange?: Moment;
  lastOilChangeKilometers?: number;
  applyPartnerDiscount?: boolean;
  applyEmployeeDiscount?: boolean;
  voucher?: {
    customer?: PortoPolicyVoucher['customer'];
    address?: PortoPolicyVoucher['customer']['address'] & {
      complement?: string;
    };
  };
  hasVoucher: boolean;
}

interface WorkOrderFormProps {
  form: FormInstance<WorkOrderFormData>;
  setCustomer: React.Dispatch<
    React.SetStateAction<CustomerReduced | undefined>
  >;
  customer?: CustomerReduced;
  vehicleToggle: VoidFunction;
  customerVehicle?: CustomerVehicleReduced;
  setCustomerVehicle: React.Dispatch<
    React.SetStateAction<CustomerVehicleReduced | undefined>
  >;
  setIsFormLoading?(value: boolean): void;
}

function WorkOrderForm({
  form,
  setCustomer,
  customer: selectedCustomer,
  vehicleToggle,
  customerVehicle,
  setCustomerVehicle,
  setIsFormLoading,
}: WorkOrderFormProps) {
  const intl = useIntl();

  const hasFuelEletric = useMemo(() => {
    return customerVehicle?.fuel?.name?.toLowerCase()?.includes('elétrico');
  }, [customerVehicle]);
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const customerIdField = Form.useWatch(['customerId'], form);
  const companyIdField = Form.useWatch('companyId', form);
  const licensePlate = Form.useWatch('licensePlate', form);
  const policyNumber = Form.useWatch(['policy', 'policyNumber'], form);
  const applyPartnerDiscount = Form.useWatch('applyPartnerDiscount', form);
  const isPortoBatteryWorkOrder = Form.useWatch(
    'isPortoBatteryWorkOrder',
    form,
  );
  const policyBenefits = Form.useWatch(
    ['policy', 'benefits'],
    form,
  ) as PortoPolicy['benefits'];
  const voucherCustomer = Form.useWatch(['voucher', 'customer'], form);

  const [customerDocument, setCustomerDocument] = useState<string>();

  const [modalSelectWorkOrderOpened, modalSelectWorkOrderAction] =
    useDisclosure();

  const [customerModalVisible, setCustomerModalVisible] = useState(false);

  const matrixDetail = useMatrix();

  const portoPoliciesList = usePortoPoliciesList(
    licensePlate,
    isAppPortocar && !!licensePlate,
  );

  const hasVoucheInPolicy = useMemo(
    () =>
      !!portoPoliciesList.data
        ?.find((policy) => policy.policyNumber === policyNumber)
        ?.benefits.some((benefit) => benefit.isVoucher),
    [portoPoliciesList.data, policyNumber],
  );

  const isValidDocumentCustomer =
    validateCpf(selectedCustomer?.cpfCnpj || '') ||
    validateCnpj(selectedCustomer?.cpfCnpj || '');

  const portoVouchers = usePortoPolicieVoucherList(
    {
      documentNumber: selectedCustomer?.cpfCnpj || customerDocument,
      customerCode: selectedCustomer?.id ? 0 : null,
    },
    isAppPortocar &&
      isValidDocumentCustomer &&
      !hasVoucheInPolicy &&
      !portoPoliciesList.isLoading,
  );

  const vouchers = useMemo(
    () => portoVouchers.data?.vouchers || [],
    [portoVouchers.data?.vouchers],
  );

  useEffect(() => {
    if (portoVouchers.data?.customer) {
      const { address, ...customer } = portoVouchers.data.customer;
      form.setFieldValue(['voucher', 'customer'], customer);
      form.setFieldValue(['voucher', 'address'], address || undefined);
    } else {
      form.setFieldValue(['voucher', 'customer'], undefined);
      form.setFieldValue(['voucher', 'address'], undefined);
    }

    form.setFieldValue('hasVoucher', !!portoVouchers.data?.vouchers.length);
  }, [portoVouchers.dataUpdatedAt, customerDocument]);

  useEffect(() => {
    if (setIsFormLoading) {
      setIsFormLoading(portoPoliciesList.isLoading);
    }
  }, [portoPoliciesList.isLoading]);

  useEffect(() => {
    if (policyNumber) {
      form.setFieldValue('applyPartnerDiscount', true);
      form.setFieldValue('applyEmployeeDiscount', false);
    }
  }, [policyNumber]);

  useEffect(() => {
    if (portoPoliciesList.data?.length) {
      const policy = portoPoliciesList.data[0];
      const { email, phone, ...restCustomer } = policy.customer;

      form.setFieldsValue({
        policy: {
          companyCode: policy.companyCode,
          policyNumber: policy.policyNumber,
          customer: { ...restCustomer, contacts: { email, phone } },
          vehicle: policy.vehicle,
          benefits: policy.benefits,
        },
      });
    } else {
      form.setFieldValue('policy', undefined);
    }
  }, [portoPoliciesList.dataUpdatedAt]);

  const [selectedCustomerType, setSelectedCustomerType] =
    useState<CustomerType>();

  const { data: configBudget, mutateAsync: configBudgetMutate } =
    useWorkorderConfigurationCompany();

  const handleSelectCompany = async (companyId: number) => {
    form.setFieldValue('estimatorId', undefined);

    if (companyId) {
      await configBudgetMutate(companyId);
    }
  };

  const workOrderData = useWorkOrders(
    {
      rows: 1,
      licensePlate: customerVehicle?.licensePlate,
      status: [
        WorkOrderComplementStatusNumber.Open,
        WorkOrderComplementStatusNumber.Authorized,
      ],
    },
    !!customerVehicle?.licensePlate,
  );

  const workOrders = flatInfiniteData(workOrderData.data);

  const handleSelectCustomerType = (type?: CustomerType) => {
    form.resetFields([
      'insurerId',
      'accidentNumber',
      'insurerDeductibleAmount',
    ]);

    setSelectedCustomerType(type);
  };

  const handleCustomerModalVisible = () => {
    setCustomerModalVisible(!customerModalVisible);
  };

  const handleCustomerFinishCallback = (customer: Customer) => {
    form.setFieldsValue({ customerId: customer.id });
    setCustomer(customer);

    setCustomerModalVisible(false);
  };

  return (
    <>
      {matrixDetail.isLoading && (
        <ModalLoadingOverlay posAbsolute messages={['Carregando dados...']} />
      )}

      <Form form={form} preserve={false}>
        <Form.Item
          hidden
          name={['policy', 'companyCode']}
          rules={
            !!portoPoliciesList.data?.length
              ? [{ required: true, message: 'Selecione uma apólice' }]
              : undefined
          }
        />
        <Form.Item hidden name={['policy', 'policyNumber']} />
        <Form.Item hidden name={['policy', 'customer']} />
        <Form.Item hidden name={['policy', 'vehicle']} />
        <Form.Item hidden name={['policy', 'benefits']} />
        <Form.Item hidden name={['voucher']} />
        <Form.Item hidden name={['voucher', 'customer']} />
        <Form.Item hidden name={['voucher', 'address']} />
        <Form.Item hidden name={'licensePlate'} />
        <Form.Item hidden name={'hasVoucher'} initialValue={false} />

        <Row gutter={24}>
          <Col span={24} md={12}>
            <Row
              gutter={[16, 8]}
              style={{
                flexDirection: isAppPortocar ? 'column-reverse' : 'column',
              }}
            >
              <Col span={24} id="workorder-create-general">
                <Row gutter={[16, 8]}>
                  <Col span={24}>
                    <Title level={4}>
                      <FormattedMessage id="workOrders" />
                    </Title>
                  </Col>

                  <Col span={24}>
                    <FloatingFormItem
                      name="companyId"
                      rules={[defaultEmptyFieldError]}
                      label={intl.formatMessage({
                        id: 'workOrders.domain.company',
                      })}
                      getValueFromEvent={(id) => {
                        form.resetFields(['estimatorId']);
                        handleSelectCompany(id);
                        return id;
                      }}
                    >
                      <CompaniesInfiniteSelectField queryEnable />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24}>
                    <FloatingFormItem
                      name="estimatorId"
                      rules={[defaultEmptyFieldError]}
                      label={intl.formatMessage({
                        id: 'workOrders.domain.complements.estimator',
                      })}
                    >
                      <EmployeesInfiniteSelectField
                        queryFilter={{
                          isActive: true,
                          companyId: companyIdField,
                          estimator: true,
                        }}
                        queryEnable={!!companyIdField}
                        disabled={!companyIdField}
                        workOrderNotSelectOtherEstimator={
                          !hasPermissions([
                            Authorities.MOVIMENTATION_WORKORDER_SELECT_ANOTHER_EMPLOYEE,
                          ])
                        }
                        key={companyIdField}
                      />
                    </FloatingFormItem>
                  </Col>

                  {isAppPortocar && (
                    <Col span={24}>
                      <Form.Item
                        name="isPortoBatteryWorkOrder"
                        noStyle
                        valuePropName="checked"
                        getValueFromEvent={({ target }) => {
                          if (target.checked) {
                            form.setFieldValue(
                              'workOrderType',
                              WorkOrderComplementType.WorkOrder,
                            );
                          }
                          return target.checked;
                        }}
                      >
                        <Checkbox>{'Orçamento de bateria (7051)'}</Checkbox>
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={12}>
                    <FloatingFormItem
                      name="workOrderType"
                      rules={[defaultEmptyFieldError]}
                      label={intl.formatMessage({
                        id: 'workOrders.domain.complements.type',
                      })}
                      initialValue={WorkOrderComplementType.WorkOrder}
                    >
                      <WorkOrderComplementTypeSelect
                        disabled={isPortoBatteryWorkOrder}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={12}>
                    <FloatingFormItem
                      name="customerType"
                      rules={[defaultEmptyFieldError]}
                      label={intl.formatMessage({
                        id: 'workOrders.domain.complements.customerType',
                      })}
                      initialValue={CustomerType.Private}
                    >
                      <Select
                        allowClear
                        style={{ width: '100%' }}
                        onChange={handleSelectCustomerType}
                        options={Object.values(CustomerType).map((a) => ({
                          label: intl.formatMessage({ id: `workOrders.${a}` }),
                          value: a,
                        }))}
                      />
                    </FloatingFormItem>
                  </Col>

                  {selectedCustomerType === CustomerType.Insurance ? (
                    <>
                      <Col span={24}>
                        <FloatingFormItem
                          name="insurerId"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.insurer',
                          })}
                          rules={[defaultEmptyFieldError]}
                        >
                          <InsurerInfiniteSelectField showCnpj />
                        </FloatingFormItem>
                      </Col>

                      <Col span={12}>
                        <FloatingFormItem
                          name="accidentNumber"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.accidentNumber',
                          })}
                        >
                          <Input />
                        </FloatingFormItem>
                      </Col>

                      <Col span={12}>
                        <FloatingFormItem
                          name="insurerDeductibleAmount"
                          initialValue={0}
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.insurerDeductibleAmount',
                          })}
                        >
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>
                    </>
                  ) : null}

                  <Col span={24} md={12}>
                    <FloatingFormItem
                      name="entryDate"
                      rules={[defaultEmptyFieldError]}
                      label={intl.formatMessage({
                        id: 'workOrders.domain.complements.entryDate',
                      })}
                      initialValue={moment()}
                    >
                      <InputDate showTime />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={12}>
                    <EstimatedLeaveDateField
                      isAlwaysGenerateActivityByWorkOrder={
                        configBudget?.alwaysGenerateActivityByWorkOrder
                      }
                    />
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Row gutter={[16, 8]}>
                  <Col span={24}>
                    <Title level={4}>
                      <FormattedMessage id="workOrders.domain.customer" />
                    </Title>
                  </Col>

                  <Col span={24} id="workorder-create-customer">
                    <Row gutter={16}>
                      <Col span={21}>
                        <FloatingFormItem
                          name="customerId"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.customer',
                          })}
                          rules={
                            !policyNumber ? [defaultEmptyFieldError] : undefined
                          }
                        >
                          <CustomersSelectField
                            onCustomerSelected={(customer) => {
                              if (
                                (customerVehicle?.customerId === 0 &&
                                  customer?.id === 0) ||
                                customerVehicle?.customerId !== customer?.id
                              ) {
                                form.resetFields(['vehicleId', 'licensePlate']);
                                setCustomerVehicle(undefined);
                              }
                              setCustomer(
                                !!customer?.id ? customer : undefined,
                              );

                              if (typeof customer?.id === 'number') {
                                setCustomerDocument(customer.cpfCnpj);
                              } else {
                                setCustomerDocument(undefined);
                              }
                            }}
                            showCpfCnpj={true}
                            onClear={() => {
                              setCustomer(undefined);
                              setCustomerVehicle(undefined);
                              form.resetFields([
                                'vehicleId',
                                'customerId',
                                'licensePlate',
                              ]);
                            }}
                            disabled={!!policyNumber}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={3} style={{ marginTop: '4px' }}>
                        <Tooltip
                          title={intl.formatMessage({
                            id: `workOrders.domain.complements.${
                              selectedCustomer
                                ? 'customerEdit'
                                : 'customerCreate'
                            }`,
                          })}
                        >
                          <Button
                            size="large"
                            icon={
                              selectedCustomer ? <FaUserEdit /> : <FaUserPlus />
                            }
                            onClick={handleCustomerModalVisible}
                            disabled={portoVouchers.isLoading}
                          />
                        </Tooltip>
                      </Col>

                      {!!selectedCustomer?.cpfCnpj && !isValidDocumentCustomer && (
                        <Col span={24}>
                          <Alert
                            style={{ width: '100%', marginBottom: 24 }}
                            type="error"
                            message={<strong>CPF/CNPJ inválido</strong>}
                            description={
                              <>
                                <p>
                                  O Nº do documento do cliente está inválido "
                                  {selectedCustomer?.cpfCnpj}"
                                </p>
                                <Button onClick={handleCustomerModalVisible}>
                                  Editar cliente
                                </Button>
                              </>
                            }
                          />
                        </Col>
                      )}

                      <Col span={24}>
                        <CustomerDefaulterAlert customerId={customerIdField} />
                      </Col>
                    </Row>
                  </Col>

                  <Col span={24} id="workorder-create-vehicle">
                    <Row gutter={16}>
                      <Col span={21}>
                        <FloatingFormItem
                          name="vehicleId"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.vehicle',
                          })}
                          getValueFromEvent={(value) => {
                            if (typeof value === 'undefined') {
                              setCustomerVehicle(undefined);
                              form.resetFields(['licensePlate']);
                            }

                            return value;
                          }}
                          extra={
                            customerIdField === 0 && isAppPortocar
                              ? 'CPF não cadastrado, gere o orçamento ou cadastre o cliente para vincular o veículo.'
                              : undefined
                          }
                        >
                          <VehicleSelectField
                            customerId={customerIdField}
                            key={customerIdField}
                            disabled={customerIdField === 0 && isAppPortocar}
                            onClear={() => {
                              setCustomerVehicle(undefined);
                              form.resetFields(['licensePlate', 'policy']);
                              form.setFieldValue('policy', undefined);
                            }}
                            queryParams={{ customerActive: true }}
                            onVehicleSelect={(vehicle) => {
                              if (vehicle) {
                                if (vehicle.id) {
                                  if (
                                    matrixDetail.data?.matrixSettings
                                      .shouldUpdateKmOnVehicle
                                  ) {
                                    form.setFieldValue(
                                      'currentKilometers',
                                      vehicle.km,
                                    );
                                  }

                                  setCustomerVehicle(vehicle);
                                }

                                form.setFieldValue(
                                  'licensePlate',
                                  vehicle.licensePlate,
                                );

                                if (!customerIdField && vehicle.id) {
                                  form.setFields([
                                    {
                                      name: 'customerId',
                                      value: vehicle.customerId,
                                      errors: [],
                                    },
                                    {
                                      name: 'vehicleId',
                                      value: vehicle.id,
                                    },
                                  ]);
                                }
                              }
                            }}
                            queryEnable={!!selectedCustomer?.id}
                            setPlateWithVehicle={isAppPortocar}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={3} style={{ marginTop: '4px' }}>
                        <Tooltip
                          title={intl.formatMessage({
                            id: `workOrders.domain.complements.${
                              customerVehicle ? 'vehicleEdit' : 'vehicleCreate'
                            }`,
                          })}
                        >
                          <Button
                            size="large"
                            disabled={
                              !selectedCustomer || customerIdField === 0
                            }
                            onClick={vehicleToggle}
                            icon={customerVehicle ? <FaEdit /> : <FaCar />}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Col>

                  {(!!customerVehicle || !!licensePlate) && (
                    <>
                      <Col span={24}>
                        <Title level={4}>
                          <FormattedMessage id="workOrders.domain.vehicle" />
                        </Title>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem
                          name="prisma"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.prisma',
                          })}
                          rules={[
                            {
                              required: configBudget?.prisma,
                              message: intl.formatMessage({
                                id: 'workOrders.fieldRequiredByConfigCompany',
                              }),
                            },
                          ]}
                        >
                          <Input />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem
                          name="currentKilometers"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.currentKilometers',
                          })}
                          rules={[
                            {
                              required:
                                isAppPortocar || configBudget?.isKmRequired,
                              message: intl.formatMessage({
                                id: 'workOrders.fieldRequiredByConfigCompany',
                              }),
                            },
                          ]}
                        >
                          <InputDecimal fractions={0} min={0} max={5000000} />
                        </FloatingFormItem>
                      </Col>

                      <Col span={8}>
                        <FloatingFormItem
                          name="tankLevel"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.tankLevel',
                          })}
                          rules={[
                            {
                              required: configBudget?.tankLevel,
                              message: intl.formatMessage({
                                id: 'workOrders.fieldRequiredByConfigCompany',
                              }),
                            },
                          ]}
                        >
                          <Select
                            allowClear
                            style={{ width: '100%' }}
                            options={Object.values(
                              WorkOrderComplementTankLevel,
                            ).map((a) => ({
                              label: intl.formatMessage({
                                id: `workOrders.${a}`,
                              }),
                              value: a,
                            }))}
                          />
                        </FloatingFormItem>
                      </Col>

                      {isAppPortocar && (
                        <>
                          <Col span={12}>
                            <FloatingFormItem
                              name="lastOilChange"
                              label={intl.formatMessage({
                                id: 'workOrders.domain.complements.lastOilChange',
                              })}
                              rules={
                                hasFuelEletric ? [] : [defaultEmptyFieldError]
                              }
                            >
                              <InputDate
                                disabledDate={(date) =>
                                  date.isAfter(moment(), 'day')
                                }
                              />
                            </FloatingFormItem>
                          </Col>

                          <Col span={12}>
                            <FloatingFormItem
                              name="lastOilChangeKilometers"
                              label={intl.formatMessage({
                                id: 'workOrders.domain.complements.lastOilChangeKilometers',
                              })}
                              rules={
                                hasFuelEletric ? [] : [defaultEmptyFieldError]
                              }
                            >
                              <InputDecimal fractions={0} min={0} />
                            </FloatingFormItem>
                          </Col>
                        </>
                      )}
                    </>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>

          {isAppPortocar && customerIdField !== 0 && (
            <Col span={24} md={12} id="workorder-create-policy">
              <div style={{ marginBottom: 24 }}>
                <Title level={4}>
                  Apólices {licensePlate ? ` • ${licensePlate}` : ''}
                </Title>

                <Form.Item shouldUpdate noStyle>
                  {({ getFieldError }) => {
                    const errors = getFieldError(['policy', 'companyCode']);
                    return errors.length ? (
                      <span style={{ color: 'red' }}>{errors[0]}</span>
                    ) : null;
                  }}
                </Form.Item>
              </div>

              <Skeleton loading={portoPoliciesList.isLoading}>
                <div className="workorder-polices">
                  {!portoPoliciesList.data?.length && !licensePlate && (
                    <Card style={{ borderRadius: 8 }}>
                      <Empty description="Informe uma placa para listar as apolices" />
                    </Card>
                  )}

                  {!portoPoliciesList.data?.length &&
                    licensePlate?.length === 7 && (
                      <Card style={{ borderRadius: 8 }}>
                        <Empty description="Não foi encontrado apolices para a placa informada" />
                      </Card>
                    )}

                  {licensePlate?.length === 7 &&
                    portoPoliciesList.data?.map((policy) => (
                      <PortoPolicyCard
                        style={{ marginTop: 16 }}
                        key={policy.policyNumber}
                        active={policy.policyNumber === policyNumber}
                        onClick={() => {
                          form.setFields([{ name: 'customerId', errors: [] }]);

                          if (policyNumber === policy.policyNumber) {
                            form.setFieldValue('policy', undefined);
                          } else {
                            const { email, phone, ...restCustomer } =
                              policy.customer;

                            form.setFieldsValue({
                              policy: {
                                companyCode: policy.companyCode,
                                policyNumber: policy.policyNumber,
                                customer: {
                                  ...restCustomer,
                                  contacts: { email, phone },
                                },
                                vehicle: policy.vehicle,
                                benefits: policy.benefits,
                              },
                            });
                          }
                        }}
                        policy={policy}
                        vehicle={{
                          ...policy.vehicle,
                          licensePlate,
                        }}
                      />
                    ))}
                </div>
              </Skeleton>
            </Col>
          )}

          {isAppPortocar && customerIdField === 0 && (
            <Col span={24} md={12}>
              <Title style={{ marginBottom: 24 }} level={4}>
                Dados do cliente por CPF
              </Title>

              <PortoCPFCustomer
                isLoading={portoVouchers.isFetching}
                address={portoVouchers.data?.customer?.address}
                customer={portoVouchers.data?.customer}
              />
            </Col>
          )}

          {!isAppPortocar && (
            <Col span={24} md={12} id="workorder-create-summary">
              <Title level={4} style={{ marginBottom: 8 }}>
                <FormattedMessage id="workOrders.create.summary" />
              </Title>

              <WorkOrderFormSummary
                customer={selectedCustomer}
                vehicle={customerVehicle}
              />

              {!!workOrders.length && (
                <div style={{ marginTop: '16px' }}>
                  <Alert
                    showIcon
                    type="warning"
                    message="Já Existem orçamentos criados (aberto, autorizado) para o veículo com a placa selecionada!"
                    description={
                      <Button
                        type="link"
                        onClick={modalSelectWorkOrderAction.open}
                      >
                        Visualizar orçamentos
                      </Button>
                    }
                  />
                </div>
              )}
            </Col>
          )}

          {isAppPortocar && (
            <>
              <Col span={24}>
                <Title level={4}>Serviços</Title>

                {!!policyNumber && (
                  <div>
                    <Form.Item
                      name="applyPartnerDiscount"
                      noStyle
                      valuePropName="checked"
                      initialValue={true}
                      getValueFromEvent={({ target }) => {
                        if (target.checked) {
                          form.setFieldValue('applyEmployeeDiscount', false);
                        }
                        return target.checked;
                      }}
                    >
                      <Checkbox>
                        Habilitar desconto obrigatório de serviços da apólice
                      </Checkbox>
                    </Form.Item>
                  </div>
                )}

                <div>
                  <Form.Item
                    name="applyEmployeeDiscount"
                    noStyle
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Checkbox disabled={applyPartnerDiscount}>
                      Habilitar desconto obrigatório de serviços para
                      funcionário Porto
                    </Checkbox>
                  </Form.Item>
                </div>
              </Col>

              {!!policyNumber && (
                <Col span={24}>
                  <Title level={4} style={{ marginTop: 16 }}>
                    Benefícios da apólice
                  </Title>

                  {!policyNumber && (
                    <Empty description="Selecione uma apólice para visualizar os benefícios" />
                  )}

                  {!!policyNumber && !policyBenefits?.length && (
                    <Empty description="Não há benefícios para esta apólice" />
                  )}

                  {!!policyNumber &&
                    policyBenefits?.map((benefit) => (
                      <Card
                        style={{ marginBottom: 16, borderRadius: 8 }}
                        bodyStyle={{ padding: 16 }}
                      >
                        <strong
                          style={{ display: 'block', fontSize: '1.05rem' }}
                        >
                          {benefit.name}
                        </strong>

                        <Row>
                          <Col span={24} xs={12} sm={8}>
                            <div
                              style={{ fontSize: '0.8rem', color: 'darkgray' }}
                            >
                              Cód. Serviço
                            </div>
                            <div>{benefit.serviceCode}</div>
                          </Col>
                          <Col span={24} xs={12} sm={8}>
                            <div
                              style={{ fontSize: '0.8rem', color: 'darkgray' }}
                            >
                              Última utilização
                            </div>
                            <div>
                              {benefit.wasUsedIn
                                ? moment(benefit.wasUsedIn).format('DD/MM/YYYY')
                                : 'Sem utilização'}
                            </div>
                          </Col>
                          <Col span={24} xs={12} sm={8}>
                            <div
                              style={{ fontSize: '0.8rem', color: 'darkgray' }}
                            >
                              Tipo
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                              }}
                            >
                              {benefit.isVoucher ? (
                                <StatusTag
                                  style={{ width: 'fit-content' }}
                                  label="Voucher"
                                  color="blue"
                                />
                              ) : (
                                <StatusTag
                                  style={{ width: 'fit-content' }}
                                  label="Benefício"
                                  color="green"
                                />
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                </Col>
              )}

              {!hasVoucheInPolicy && !!vouchers.length && (
                <Col span={24}>
                  <PortoVoucherList
                    isLoading={portoVouchers.isLoading}
                    vouchers={vouchers || []}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
      </Form>

      <CustomerSimplifiedRegisterModal
        visible={customerModalVisible}
        handleVisible={handleCustomerModalVisible}
        customerId={selectedCustomer?.id}
        onFinishCallback={handleCustomerFinishCallback}
        initialValues={
          customerIdField === 0 && customerDocument && !portoVouchers.isLoading
            ? {
                cpfCnpj: customerDocument,
                name: voucherCustomer?.customerName,
                birthDate: voucherCustomer?.birthDate
                  ? moment(voucherCustomer?.birthDate)
                  : undefined,
                personType: PersonType.Natural,
                genderType: !voucherCustomer?.sexCode
                  ? undefined
                  : voucherCustomer?.sexCode === 'M'
                  ? GenderType.Man
                  : GenderType.Woman,
                phone: voucherCustomer?.phoneExtension,
                email: voucherCustomer?.contact,
              }
            : undefined
        }
      />

      <WorkOrdersListModal
        open={modalSelectWorkOrderOpened}
        onCancel={modalSelectWorkOrderAction.close}
        workOrderIsExists
        queryFilter={{
          status: [
            WorkOrderComplementStatusNumber.Open,
            WorkOrderComplementStatusNumber.Authorized,
          ],
          licensePlate: customerVehicle?.licensePlate,
        }}
        queryEnabled={!!customerVehicle?.licensePlate}
      />
    </>
  );
}

export default WorkOrderForm;
